import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, IconButton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useDndMonitor} from "@dnd-kit/core";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {useQueryClient} from "react-query";
import {Reservation,} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {NotificationTrigger} from "crqlar-react-common/dist/models/sector/sector.model";
import {sendReservationNotification} from "../../../../../utils/reservationNotification.util";
import {useDispatch} from "react-redux";
import {useCurrentAppId} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {ReservationActions} from "../../../../../store/reservations.reducer";
import {FloorplanListHalfboardDTO} from "crqlar-react-common/dist/models/floorplan/floorplanListHalfboard.dto";
import {DragData, FloorplanTable, TableOverlay} from "../types";
import {MixpanelService} from "crqlar-react-common/dist/services/analytics/mixpanel.service";
import ConnectionLines from "./ConnectionLines";
import TableOverlayLayerItem from "./TableOverlayLayerItem";
import {useReadStore} from "../hooks/useReadStore";
import {
    isReservationOngoingAt,
    isReservationUpcomingAt,
    reservationIntersectsTimeframe,
    useTimeframe
} from "../hooks/useTimeframe";
import type {DayUpdate} from "./dialogs/GroupReservationConflictDialog";
import GroupReservationConflictDialog from "./dialogs/GroupReservationConflictDialog";
import ConflictTableDialog from "./dialogs/ConflictTableDialog";
import SharedTableConfirmDialog from "./dialogs/SharedTableConfirmDialog";
import ConfirmDropEmailDialog from "./dialogs/ConfirmDropEmailDialog";
import ReservationDayPeriodDialog from "../../../ReservationDayPeriodDialog";
import ReservationDialog from "../../../reservationDialog/ReservationDialog";
import {
    buildTableReservationsMap,
    DropConflictResult,
    dropDecidesGuestEmail,
    resolveDropDecision,
} from "../utils/drop-utils";
import {getHalfboardRateLabel, getOrderedReservationProfileIds, getPrimaryProfileId} from "../utils/reservation-utils";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import {useHalfboardReservationBuilder} from "../hooks/useHalfboardReservationBuilder";
import {FloorplanReservationItemDTO} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {formatDateHM, isSameDay} from "crqlar-react-common/dist/utils/date.util";

interface TableOverlayLayerProps {
    onConflictWillOpen: () => void;
    disableTableCardDrag?: boolean;
}

type Period = 'breakfast' | 'lunch' | 'dinner' | 'breakfast-plus-dinner' | 'blank';

interface PendingConflictDrop {
    data: DragData;
    targetTableId: string;
    result: DropConflictResult;
    neededSeats: number;
}

interface PendingSharedTableDrop {
    data: Extract<DragData, { type: "unassigned" | "assigned" }>;
    targetTableId: string;
    result: DropConflictResult;
}

interface GroupConflictDialogState {
    reservation: Reservation;
    original: Reservation;
    linked: Reservation[];
    accommodation?: Reservation;
    profiles: Profile[];
    accommodationResources?: Resource[];
}

interface PendingHalfboardDrop {
    halfboard: FloorplanListHalfboardDTO;
    targetTable: FloorplanTable;
}

// A drop that is ready to be persisted and only waits for the staff's "send email?" answer.
interface PendingEmailDrop {
    data: Extract<DragData, { type: "unassigned" | "assigned" }>;
    targetTable: FloorplanTable;
}

const EMPTY_RESERVATIONS: FloorplanReservationItemDTO[] = [];

// Normalize reservation values so we can compare "status only" edits reliably.
const normalizeComparisonValue = (value: unknown): unknown => {
    if (value instanceof Date) return value.toISOString();
    if (Array.isArray(value)) return value.map(normalizeComparisonValue);
    if (value && typeof value === "object") {
        return Object.keys(value as Record<string, unknown>)
            .sort()
            .reduce<Record<string, unknown>>((acc, key) => {
                const normalized = normalizeComparisonValue((value as Record<string, unknown>)[key]);
                if (normalized !== undefined) {
                    acc[key] = normalized;
                }
                return acc;
            }, {});
    }
    return value;
};

const serializeReservationWithoutStatus = (reservation?: Reservation | null): string => {
    if (!reservation) return "";
    const {status: _status, ...rest} = reservation;
    return JSON.stringify(normalizeComparisonValue(rest));
};

const TableOverlayLayer: React.FC<TableOverlayLayerProps> = ({
                                                                 onConflictWillOpen,
                                                                 disableTableCardDrag = false,
                                                             }) => {
    const {t} = useTranslation();
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    // Read-only users may not move/assign reservations on the floorplan via drag & drop.
    // (Dragging is also disabled at the DndContext in ReadCanvasView; this is a backstop.)
    const readOnly = useReadOnly();
    const {canvas, restaurantId} = useFloorplanContext();
    const appId = useCurrentAppId();
    const {
        date,
        timeframeStart,
        timeframeEnd,
        referenceTime,
        setCustomTimeframeForDate,
    } = useTimeframe();
    const {buildForPeriod} = useHalfboardReservationBuilder(restaurantId);

    // Shows a snackbar with a "Show" button when none of the reservations are visible in the current timeframe.
    // If at least one reservation is already visible (e.g. dinner in breakfast-plus-dinner), no snackbar is shown.
    const notifyIfNotVisible = useCallback((reservations: Reservation | Reservation[]) => {
        const list = Array.isArray(reservations) ? reservations : [reservations];

        const isVisible = (reservation: Reservation) => {
            const start = reservation.expectedStartDate;
            const end = reservation.expectedEndDate;
            if (!start || !end) return false;

            const startD = new Date(start);
            const onSameDay = isSameDay(startD, date);
            return onSameDay && reservationIntersectsTimeframe(start, end, timeframeStart, timeframeEnd);
        };

        // If any reservation is already visible on the layout, don't notify
        if (list.some(isVisible)) return;

        // None are visible — pick the best one for the "Show" navigation
        const valid = list.filter(r => r.expectedStartDate && r.expectedEndDate);
        if (valid.length === 0) return;

        // Compute a timeframe that covers all reservations for the "Show" navigation
        const allStarts = valid.map(r => new Date(r.expectedStartDate!).getTime());
        const allEnds = valid.map(r => new Date(r.expectedEndDate!).getTime());
        const earliestStart = new Date(Math.min(...allStarts));
        const latestEnd = new Date(Math.max(...allEnds));

        // Collect table IDs from all reservations for highlighting
        const tableIds = valid.flatMap(r =>
            (r.resourceIds ?? []).filter(id => id !== restaurantId),
        ) as string[];

        enqueueSnackbar(t('Reservation is outside the current time period'), {
            variant: 'info',
            autoHideDuration: 8000,
            action: (snackbarId) => {
                // Switch the timeframe to show the non-visible reservation(s)
                const handleShow = () => {
                    closeSnackbar(snackbarId);
                    setCustomTimeframeForDate(earliestStart, {
                        start: formatDateHM(earliestStart),
                        end: formatDateHM(latestEnd),
                    });

                    // Highlight the affected tables after the timeframe change renders
                    if (tableIds.length > 0) {
                        setTimeout(() => useReadStore.getState().highlightTable(tableIds), 300);
                    }
                };

                return (<>
                    <Button color="inherit" size="small" onClick={handleShow}>
                        {t('Show')}
                    </Button>
                    <IconButton size="small" color="inherit"
                                onClick={() => closeSnackbar(snackbarId)}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </>);
            },
        });
    }, [date, timeframeStart, timeframeEnd, enqueueSnackbar, closeSnackbar, t, restaurantId, setCustomTimeframeForDate]);

    const floorplanTables = useReadStore((state) => state.floorplanTables);
    const assignedReservations = useReadStore((state) => state.assignedReservations);

    const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
    const [selectedReservationSnapshot, setSelectedReservationSnapshot] = useState("");
    const [selectedReservationOriginalStatus, setSelectedReservationOriginalStatus] = useState<Reservation["status"]>();
    const [pendingConflictDrop, setPendingConflictDrop] = useState<PendingConflictDrop | null>(null);
    const [pendingSharedTableDrop, setPendingSharedTableDrop] = useState<PendingSharedTableDrop | null>(null);
    const [groupConflictDialog, setGroupConflictDialog] = useState<GroupConflictDialogState | null>(null);
    const [activeDragData, setActiveDragData] = useState<DragData | null>(null);
    const [activeReservationTooltipTableId, setActiveReservationTooltipTableId] = useState<string | null>(null);
    const [pendingHalfboard, setPendingHalfboard] = useState<PendingHalfboardDrop | null>(null);
    const [pendingEmailDrop, setPendingEmailDrop] = useState<PendingEmailDrop | null>(null);
    const [halfboardDraft, setHalfboardDraft] = useState<Reservation | undefined>();
    const [halfboardLinked, setHalfboardLinked] = useState<Reservation[] | undefined>();
    const [halfboardCheckoutDate, setHalfboardCheckoutDate] = useState<Date | undefined>();
    const wrapperRef = useRef<HTMLDivElement>(null);

    const handleReservationTooltipActiveChange = useCallback((tableId: string, active: boolean) => {
        setActiveReservationTooltipTableId(currentTableId => {
            if (active) return tableId;
            return currentTableId === tableId ? null : currentTableId;
        });
    }, []);

    const invalidateQueries = useCallback(() => {
        queryClient.invalidateQueries({queryKey: ["floorplanAssigned"]});
        queryClient.invalidateQueries({queryKey: ["floorplanUnassigned"]});
        queryClient.invalidateQueries({queryKey: ["floorplanHalfboardProfiles"]});
    }, [queryClient]);

    // Capture the original floorplan-dialog reservation so undo can restore just its prior status.
    const openSelectedReservation = useCallback((reservation: Reservation) => {
        setSelectedReservation(reservation);
        setSelectedReservationSnapshot(serializeReservationWithoutStatus(reservation));
        setSelectedReservationOriginalStatus(reservation.status);
    }, []);

    const resetSelectedReservation = useCallback(() => {
        setSelectedReservation(null);
        setSelectedReservationSnapshot("");
        setSelectedReservationOriginalStatus(undefined);
    }, []);

    // Only show undo when the floorplan dialog save changed status and nothing else.
    const showStatusUndoSnackbar = useCallback((reservation?: Reservation) => {
        if (!reservation?.id) return;
        if (!selectedReservationSnapshot) return;
        if (selectedReservationOriginalStatus === reservation.status) return;

        const savedSnapshot = serializeReservationWithoutStatus(reservation);
        if (savedSnapshot !== selectedReservationSnapshot) return;

        closeSnackbar();
        enqueueSnackbar(t("Reservation updated"), {
            variant: "success",
            autoHideDuration: 5000,
            action: (snackbarId) => {
                const handleUndo = async () => {
                    closeSnackbar(snackbarId);
                    try {
                        await ReservationService.updateReservation({
                            ...reservation,
                            status: selectedReservationOriginalStatus,
                        }, true);
                    } catch (error) {
                        console.error(error);
                    }
                    invalidateQueries();
                };

                return (<>
                    <Button color="inherit" size="small" onClick={handleUndo}>
                        {t("Undo")}
                    </Button>
                    <IconButton size="small" color="inherit"
                                onClick={() => closeSnackbar(snackbarId)}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </>);
            },
        });
    }, [
        closeSnackbar,
        enqueueSnackbar,
        invalidateQueries,
        selectedReservationOriginalStatus,
        selectedReservationSnapshot,
        t,
    ]);

    /**
     * Sends the guest's "reservation updated" mail for a drop, and says what happened.
     *
     * Deliberately not the `allowNotify=true` flag on the update endpoint: that flag is dropped
     * entirely for reservations with a reservationGroupId (ReservationServiceV3.update ->
     * isSerialMember, so a serial stay is not mailed once per linked day), it only queues a
     * SCHEDULED correspondence message instead of sending it, and it returns no result — so a
     * sector without an UPDATED template looked exactly like a successful send. resendNotification
     * has neither limitation and reports the relay status back.
     */
    const sendUpdateEmail = useCallback(async (reservationId?: string) => {
        if (!reservationId) return;

        const {message, variant} = await sendReservationNotification(
            reservationId,
            NotificationTrigger.UPDATED,
            t,
        );
        enqueueSnackbar(message, {variant, autoHideDuration: 5000});
    }, [enqueueSnackbar, t]);

    // `sendEmail` is the staff's explicit answer to the "send email?" confirmation shown before
    // every drop. It is ignored on the grouped-reservation path below, which hands the choice to
    // GroupReservationConflictDialog instead.
    const executeReservationDrop = useCallback(async (
        data: Extract<DragData, { type: "unassigned" | "assigned" }>,
        targetTable: FloorplanTable,
        sendEmail: boolean,
    ) => {
        const reservation = data.reservation;
        const sourceTableId = data.type === "assigned" ? data.sourceTableId : undefined;
        const reservationToUpdate = reservation.restaurantReservation;
        const store = useReadStore.getState();

        // Optimistic update: move the reservation in local store before the API call
        if (sourceTableId) {
            // Reassigning from one table to another — swap the table resource in place
            store.patch({
                assignedReservations: store.assignedReservations.map((current) =>
                    current.reservationId === reservation.reservationId
                        ? {
                            ...current,
                            tableResources: [
                                ...(current.tableResources || []).filter((tableResource) => tableResource.id !== sourceTableId),
                                targetTable.resource,
                            ],
                        }
                        : current,
                ),
            });
        } else {
            // New assignment from unassigned — add to assigned and remove from unassigned
            store.patch({
                assignedReservations: [
                    ...store.assignedReservations,
                    {...reservation, tableResources: [targetTable.resource]},
                ],
                unassignedReservations: store.unassignedReservations.filter(
                    (current) => current.reservationId !== reservation.reservationId,
                ),
            });
        }

        const existingTableIds = (reservationToUpdate.resourceIds ?? [])
            .filter((resourceId) => resourceId !== restaurantId && resourceId !== sourceTableId)
            .filter(Boolean) as string[];

        const updatedReservation: Reservation = {
            ...reservationToUpdate,
            resourceIds: [restaurantId, ...existingTableIds, targetTable.resource.id!],
        };

        // Part of a group (e.g. multi-day stay) — fetch linked reservations and show conflict dialog.
        // Two parallel calls:
        //   1) all linked reservations in this booking (by reservationGroupId)
        //   2) the guest's main accommodation reservation directly (so we always have a
        //      reliable stay start/end, even if it's missing from the linked list).
        if (reservationToUpdate.reservationGroupId) {
            try {
                const accommodationId = reservation.accommodationReservations?.[0]?.id;
                const [linkedResp, accommodationResp] = await Promise.all([
                    ReservationService.getAllReservations({
                        reservationGroupId: [reservationToUpdate.reservationGroupId],
                        limit: {offset: 0, limit: 100},
                    }),
                    accommodationId
                        ? ReservationService.getReservationById(accommodationId).catch(() => undefined)
                        : Promise.resolve(undefined),
                ]);
                setGroupConflictDialog({
                    reservation: updatedReservation,
                    original: reservationToUpdate,
                    linked: linkedResp.content || [],
                    accommodation: accommodationResp,
                    profiles: reservation.profiles ?? [],
                    accommodationResources: reservation.accommodationResources,
                });
            } catch (error) {
                console.error(error);
                invalidateQueries();
            }
            return;
        }

        try {
            // Always silent — the guest mail is sent explicitly below so its result is visible.
            await ReservationService.updateReservation(updatedReservation, false);
        } catch (error) {
            console.error(error);
            invalidateQueries();
            return;
        }

        invalidateQueries();

        MixpanelService.getInstance().track('floorplan dnd', {
            action: 'drop_success',
            restaurantId,
            source: sourceTableId ? 'assigned' : 'unassigned',
            targetTableId: targetTable.resource.id,
            targetTableName: targetTable.resource.name,
            sourceTableId: sourceTableId ?? null,
            reservationId: reservation.reservationId,
        });

        const originalReservation = {...reservationToUpdate};
        closeSnackbar();
        notifyIfNotVisible(reservationToUpdate);
        enqueueSnackbar(
            t('Reservation assigned to {{table}}', {table: targetTable.resource.name}),
            {
                variant: 'success',
                autoHideDuration: 5000,
                action: (snackbarId) => {
                    const handleUndo = async () => {
                        closeSnackbar(snackbarId);
                        try {
                            await ReservationService.updateReservation(originalReservation, false);
                            // Undo mirrors the original choice: a silent move is undone silently,
                            // an emailed move sends the guest the restored times.
                            if (sendEmail) await sendUpdateEmail(originalReservation.id);
                        } catch (error) {
                            console.error(error);
                        }
                        invalidateQueries();
                    };

                    return (<>
                        <Button color="inherit" size="small" onClick={handleUndo}>
                            {t('Undo')}
                        </Button>
                        <IconButton size="small" color="inherit"
                                    onClick={() => closeSnackbar(snackbarId)}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </>);
                },
            },
        );

        if (sendEmail) await sendUpdateEmail(reservationToUpdate.id);
    }, [restaurantId, invalidateQueries, t, enqueueSnackbar, closeSnackbar, notifyIfNotVisible, sendUpdateEmail]);

    // Entry point for every reservation drop: dropping a card must never send a guest email on its
    // own, so the drop is parked until the staff answers the confirmation dialog. Grouped
    // reservations skip it — GroupReservationConflictDialog asks at its own confirm step.
    const requestReservationDrop = useCallback((
        data: Extract<DragData, { type: "unassigned" | "assigned" }>,
        targetTable: FloorplanTable,
    ) => {
        if (!dropDecidesGuestEmail(data)) {
            void executeReservationDrop(data, targetTable, false);
            return;
        }

        setPendingEmailDrop({data, targetTable});
    }, [executeReservationDrop]);

    // Builds a halfboard reservation draft for the selected meal period and opens the reservation dialog.
    const handlePeriodConfirm = useCallback((period: Period) => {
        localStorage.setItem('selectedEatPeriod', period)

        if (!pendingHalfboard) return;

        const {halfboard, targetTable} = pendingHalfboard;
        const targetTableId = targetTable.resource.id!;
        setPendingHalfboard(null);

        const accomReservation = halfboard.accommodationReservation;
        const checkIn = accomReservation?.actualStartDate || accomReservation?.expectedStartDate;
        const checkOut = accomReservation?.actualEndDate || accomReservation?.expectedEndDate;

        // The stay's guests are `primaryProfileId + profileIds`: connectors keep the booker on the
        // reservation's primary without always repeating them in profileIds, so reading profileIds
        // alone silently left that guest out of the table reservation created from this drag.
        const stayProfileIds = getOrderedReservationProfileIds(accomReservation);

        const {draft, linked, checkoutDate} = buildForPeriod({
            profileIds: stayProfileIds.length > 0 ? stayProfileIds : halfboard.profileIds ?? [],
            primaryProfileId: getPrimaryProfileId(accomReservation) ?? halfboard.profileIds?.[0],
            checkInDate: checkIn ? new Date(checkIn) : new Date(),
            targetTableId,
            guestCounts: accomReservation?.guestCounts,
            checkOutDate: checkOut ? new Date(checkOut) : undefined,
            period,
        });

        // Carry over notes and meta (e.g. event) from the accommodation reservation
        const accomMeta = accomReservation?.meta;
        draft.notes = accomReservation?.notes;
        draft.meta = {...accomMeta, eatPeriod: draft.meta?.eatPeriod};
        if (linked) {
            for (const r of linked) {
                r.notes = accomReservation?.notes;
                r.meta = {...accomMeta, eatPeriod: r.meta?.eatPeriod};
            }
        }

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload: {
                checkInDate: checkIn ? new Date(checkIn) : undefined,
                checkOutDate: checkoutDate,
            },
        });

        setHalfboardCheckoutDate(checkoutDate);
        setHalfboardLinked(linked);
        setHalfboardDraft(draft);
    }, [pendingHalfboard, buildForPeriod, dispatch, appId]);

    // User confirmed the conflict dialog — proceed with the drop or open the halfboard period picker.
    const handleConflictDropConfirm = useCallback(async (sendEmail: boolean) => {
        if (!pendingConflictDrop) return;

        const {data, result} = pendingConflictDrop;
        setPendingConflictDrop(null);

        if (data.type === "unassigned" || data.type === "assigned") {
            await executeReservationDrop(data, result.targetTable, sendEmail);
            return;
        }

        setPendingHalfboard({
            halfboard: data.halfboard,
            targetTable: result.targetTable
        });
    }, [pendingConflictDrop, executeReservationDrop]);

    // User confirmed sharing the table — proceed with assigning the reservation.
    const handleSharedTableConfirm = useCallback(async (sendEmail: boolean) => {
        if (!pendingSharedTableDrop) return;

        const {data, result} = pendingSharedTableDrop;
        setPendingSharedTableDrop(null);

        await executeReservationDrop(data, result.targetTable, sendEmail);
    }, [pendingSharedTableDrop, executeReservationDrop]);

    // Staff answered the "send email?" dialog for a conflict-free drop.
    const handleEmailDropConfirm = useCallback(async (sendEmail: boolean) => {
        if (!pendingEmailDrop) return;

        const {data, targetTable} = pendingEmailDrop;
        setPendingEmailDrop(null);

        await executeReservationDrop(data, targetTable, sendEmail);
    }, [pendingEmailDrop, executeReservationDrop]);


    // Updates all linked reservations in a group to their new tables (with undo support).
    const handleGroupConflictConfirm = useCallback(async (updates: DayUpdate[], sendEmail: boolean) => {
        const originals = updates.map((update) => ({...update.reservation}));
        const dialogReservation = groupConflictDialog?.reservation;
        // The dragged reservation carries the times the guest should read in the mail.
        const notifyReservationId = dialogReservation?.id ?? updates[0]?.reservation.id;

        try {
            for (const {reservation, newTableId} of updates) {
                const currentTableId = (reservation.resourceIds ?? [])
                    .find(id => id !== restaurantId);
                const existingTableIds = (reservation.resourceIds ?? [])
                    .filter(id => id !== restaurantId && id !== currentTableId)
                    .filter(Boolean) as string[];

                // Always silent: linked members are suppressed backend-side anyway, and the one
                // guest mail is sent once below.
                await ReservationService.updateReservation({
                    ...reservation,
                    resourceIds: [restaurantId, ...existingTableIds, newTableId],
                }, false);
            }

            if (sendEmail) await sendUpdateEmail(notifyReservationId);

            setGroupConflictDialog(null);
            closeSnackbar();
            if (dialogReservation) notifyIfNotVisible(dialogReservation);
            enqueueSnackbar(t('Linked reservations updated'), {
                variant: 'success',
                autoHideDuration: 5000,
                action: (snackbarId) => {
                    const handleUndo = async () => {
                        closeSnackbar(snackbarId);
                        try {
                            for (const original of originals) {
                                await ReservationService.updateReservation(original, false);
                            }
                            // Undo mirrors the original choice: a silent move is undone silently,
                            // an emailed move sends the corrected times.
                            if (sendEmail) await sendUpdateEmail(notifyReservationId);
                        } catch (error) {
                            console.error(error);
                        }
                        invalidateQueries();
                    };

                    return (<>
                        <Button color="inherit" size="small" onClick={handleUndo}>
                            {t('Undo')}
                        </Button>
                        <IconButton size="small" color="inherit"
                                    onClick={() => closeSnackbar(snackbarId)}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </>);
                },
            });
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('Failed to update linked reservations'), {
                variant: 'error',
                autoHideDuration: 5000,
            });
        } finally {
            invalidateQueries();
        }
    }, [restaurantId, invalidateQueries, t, enqueueSnackbar, closeSnackbar, notifyIfNotVisible, groupConflictDialog, sendUpdateEmail]);

    // Cleans up halfboard state after the reservation dialog saves successfully.
    const handleHalfboardDraftSaved = useCallback(() => {
        setHalfboardDraft(undefined);
        setHalfboardLinked(undefined);
        setHalfboardCheckoutDate(undefined);
        invalidateQueries();
        closeSnackbar();
        const allReservations = halfboardLinked ?? (halfboardDraft ? [halfboardDraft] : []);
        if (allReservations.length > 0) notifyIfNotVisible(allReservations);
        enqueueSnackbar(t('Reservation created'), {
            variant: 'success',
            autoHideDuration: 3000
        });
    }, [invalidateQueries, closeSnackbar, enqueueSnackbar, t, notifyIfNotVisible, halfboardDraft, halfboardLinked]);

    useDndMonitor({
        onDragStart: (event) => {
            const data = event.active.data.current as DragData;
            setActiveDragData(data);
            MixpanelService.getInstance().track('floorplan dnd', {
                action: 'drag_start',
                restaurantId,
                source: data.type,
                reservationId: data.type !== 'halfboard' ? data.reservation?.reservationId : undefined,
                sourceTableId: data.type === 'assigned' ? data.sourceTableId : undefined,
            });
        },
        onDragEnd: (event) => {
            setActiveDragData(null);
            // Read-only users cannot persist any drag result.
            if (readOnly) return;
            const {active: activeItem, over} = event;
            if (!over) {
                MixpanelService.getInstance().track('floorplan dnd', {
                    action: 'drop_missed',
                    restaurantId,
                    source: (activeItem.data.current as DragData).type,
                });
                return;
            }

            const data = activeItem.data.current as DragData;
            const targetTableId = over.id as string;
            const decision = resolveDropDecision(
                data,
                targetTableId,
                floorplanTables,
                assignedReservations,
            );

            switch (decision.action) {
                // No conflicts — assign directly
                case "execute_reservation_drop":
                    onConflictWillOpen();
                    requestReservationDrop(
                        decision.data,
                        decision.result.targetTable,
                    );
                    return;

                // Table has conflicting reservations — ask user
                case "open_conflict_dialog":
                    MixpanelService.getInstance().track('floorplan dnd', {
                        action: 'drop_conflict',
                        restaurantId,
                        targetTableId
                    });
                    onConflictWillOpen();
                    setPendingConflictDrop({
                        data: decision.data,
                        targetTableId: decision.targetTableId,
                        result: decision.result,
                        neededSeats: decision.neededSeats,
                    });
                    return;

                // Table already occupied — confirm sharing
                case "open_shared_table_dialog":
                    MixpanelService.getInstance().track('floorplan dnd', {
                        action: 'drop_shared_table',
                        restaurantId,
                        targetTableId
                    });
                    onConflictWillOpen();
                    setPendingSharedTableDrop({
                        data: decision.data,
                        targetTableId: decision.targetTableId,
                        result: decision.result,
                    });
                    return;

                // Halfboard guest — ask which meal period
                case "open_halfboard_period":
                    MixpanelService.getInstance().track('floorplan dnd', {
                        action: 'drop_halfboard',
                        restaurantId,
                        targetTableId
                    });
                    onConflictWillOpen();
                    setPendingHalfboard({
                        halfboard: decision.halfboard,
                        targetTable: decision.targetTable,
                    });
                    return;

                // Drop target is invalid or same table — ignore
                case "noop":
                default:
                    MixpanelService.getInstance().track('floorplan dnd', {
                        action: 'drop_noop',
                        restaurantId,
                        targetTableId
                    });
                    return;
            }
        },
        onDragCancel: () => {
            setActiveDragData(null);
            MixpanelService.getInstance().track('floorplan dnd', {action: 'drag_cancel', restaurantId});
        },
    });

    const tableOverlays = useMemo<TableOverlay[]>(() => {
        return floorplanTables.map(({id, resource, fabricObject}) => {
            const bounds = fabricObject.getBoundingRect();

            return {
                id,
                x: bounds.left,
                y: bounds.top,
                width: bounds.width,
                height: bounds.height,
                angle: fabricObject.angle ?? 0,
                capacity: resource.capacity ?? 4,
                resource,
                textColor: fabricObject.floorPlanItem?.table?.canvas.textColor,
            };
        });
    }, [floorplanTables]);

    // Map each table ID to its list of assigned reservations
    const tableReservationsById = useMemo(
        () => buildTableReservationsMap(assignedReservations),
        [assignedReservations],
    );

    // Collect reservation IDs that are "current" (displayed) on at least one table.
    // Mirrors the ongoing > upcoming priority in TableOverlayLayerItem.
    const visibleReservationIds = useMemo(() => {
        const ids = new Set<string>();
        tableReservationsById.forEach((tableReservations: FloorplanReservationItemDTO[]) => {
            // Only consider reservations that fall within the selected timeframe
            const visible = tableReservations.filter((item: FloorplanReservationItemDTO) =>
                reservationIntersectsTimeframe(
                    item.restaurantReservation?.expectedStartDate,
                    item.restaurantReservation?.expectedEndDate,
                    timeframeStart,
                    timeframeEnd,
                ),
            );

            // Priority 1: a reservation that is currently ongoing (started but not ended)
            const ongoing = visible.find((item: FloorplanReservationItemDTO) =>
                isReservationOngoingAt(
                    item.restaurantReservation?.expectedStartDate,
                    item.restaurantReservation?.expectedEndDate,
                    referenceTime,
                ),
            );
            // Priority 2: the next upcoming reservation (starts after referenceTime)
            const upcoming = visible.find((item: FloorplanReservationItemDTO) =>
                isReservationUpcomingAt(
                    item.restaurantReservation?.expectedStartDate,
                    referenceTime,
                ),
            );

            const current = ongoing ?? upcoming;
            if (current?.reservationId) ids.add(current.reservationId);
        });
        return ids;
    }, [tableReservationsById, timeframeStart, timeframeEnd, referenceTime]);

    // Keep the HTML overlay wrapper in sync with the canvas pan/zoom transform.
    useEffect(() => {
        if (!canvas) return;

        const syncTransform = () => {
            const wrapper = wrapperRef.current;
            if (!wrapper) return;
            const viewportTransform = canvas.viewportTransform;
            if (!viewportTransform) return;
            wrapper.style.transform = `matrix(${viewportTransform[0]}, 0, 0, ${viewportTransform[3]}, ${viewportTransform[4]}, ${viewportTransform[5]})`;
        };

        syncTransform();
        canvas.on('after:render', syncTransform);
        return () => {
            canvas.off('after:render', syncTransform);
        };
    }, [canvas]);

    return (
        <>
            <div
                ref={wrapperRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    transformOrigin: '0 0',
                    pointerEvents: 'none',
                    zIndex: 2,
                }}
            >
                <ConnectionLines tableOverlays={tableOverlays}
                                 visibleReservationIds={visibleReservationIds}/>

                {tableOverlays.map((overlay) => (
                    <TableOverlayLayerItem
                        key={overlay.id}
                        overlay={overlay}
                        activeDragData={activeDragData}
                        disableTableCardDrag={disableTableCardDrag}
                        tableReservations={tableReservationsById.get(overlay.id) ?? EMPTY_RESERVATIONS}
                        reservationTooltipActive={activeReservationTooltipTableId === overlay.id}
                        onReservationTooltipActiveChange={handleReservationTooltipActiveChange}
                        onClickEdit={openSelectedReservation}
                    />
                ))}
            </div>

            {/* Seat conflict warning */}
            <ConflictTableDialog
                open={!!pendingConflictDrop}
                onClose={() => setPendingConflictDrop(null)}
                onConfirm={handleConflictDropConfirm}
                conflicts={pendingConflictDrop?.result.conflicts ?? []}
                capacity={pendingConflictDrop?.result.capacity ?? 0}
                currentGuestCount={pendingConflictDrop?.result.currentGuestCount ?? 0}
                neededSeats={pendingConflictDrop?.neededSeats ?? 0}
                targetTableName={pendingConflictDrop?.result.targetTable.resource.name ?? ''}
                dragData={pendingConflictDrop?.data}
            />

            {/* Send-email choice for a conflict-free drop */}
            <ConfirmDropEmailDialog
                open={!!pendingEmailDrop}
                onClose={() => setPendingEmailDrop(null)}
                onConfirm={handleEmailDropConfirm}
                targetTableName={pendingEmailDrop?.targetTable.resource.name ?? ''}
                dragData={pendingEmailDrop?.data}
            />

            {/* Shared table confirmation */}
            {pendingSharedTableDrop && (
                <SharedTableConfirmDialog
                    open={true}
                    onClose={() => setPendingSharedTableDrop(null)}
                    onConfirm={handleSharedTableConfirm}
                    conflicts={pendingSharedTableDrop.result.conflicts}
                    targetTableName={pendingSharedTableDrop.result.targetTable.resource.name ?? ''}
                    dragData={pendingSharedTableDrop.data}
                />
            )}

            {/* Linked group table reassignment */}
            {groupConflictDialog && (
                <GroupReservationConflictDialog
                    open={!!groupConflictDialog}
                    onClose={() => {
                        setGroupConflictDialog(null);
                        invalidateQueries();
                    }}
                    onConfirm={handleGroupConflictConfirm}
                    reservation={groupConflictDialog.reservation}
                    original={groupConflictDialog.original}
                    linked={groupConflictDialog.linked}
                    accommodation={groupConflictDialog.accommodation}
                    floorplanTables={floorplanTables}
                    profiles={groupConflictDialog.profiles}
                    accommodationResources={groupConflictDialog.accommodationResources}
                />
            )}

            {/* Halfboard meal period picker */}
            <ReservationDayPeriodDialog
                open={!!pendingHalfboard}
                onClose={() => setPendingHalfboard(null)}
                onConfirm={handlePeriodConfirm}
                draggedReservation={pendingHalfboard ? {
                    reservation: pendingHalfboard.halfboard.accommodationReservation,
                    profiles: pendingHalfboard.halfboard.profiles,
                    accommodationResources: pendingHalfboard.halfboard.accommodationResources,
                    ageCategories: pendingHalfboard.halfboard.ageCategories,
                    accommodationServiceName: getHalfboardRateLabel(pendingHalfboard.halfboard),
                    targetTableName: pendingHalfboard.targetTable.resource.name,
                } : undefined}
            />

            {/* Halfboard reservation editor */}
            <ReservationDialog
                open={!!halfboardDraft}
                onClose={() => {
                    setHalfboardDraft(undefined);
                    setHalfboardLinked(undefined);
                    setHalfboardCheckoutDate(undefined);
                    invalidateQueries();
                }}
                reservation={halfboardDraft}
                onSave={() => {
                    handleHalfboardDraftSaved();
                }}
                onChangeReservation={setHalfboardDraft}
                linkedReservationsParam={halfboardLinked}
                defaultMultidayUntil={halfboardCheckoutDate}
            />

            {/* Edit existing reservation */}
            <ReservationDialog
                open={!!selectedReservation}
                onClose={() => {
                    resetSelectedReservation();
                    invalidateQueries();
                }}
                reservation={selectedReservation ?? undefined}
                onSave={(reservation) => {
                    showStatusUndoSnackbar(reservation ?? selectedReservation ?? undefined);
                    resetSelectedReservation();
                    invalidateQueries();
                }}
                onChangeReservation={openSelectedReservation}
            />
        </>
    );
};

export default TableOverlayLayer;
